import { observer } from "mobx-react";
import React from "react";
import { RouteComponentProps } from "react-router";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import { generatePath, withRouter } from "react-router-dom";
import { computed } from "mobx";
import ActiveProgramStore from "stores/ActiveProgramStore";
import { TableSortType } from "stores/templates/TableStore";
import { SNACKBAR_TYPES } from "components/Snackbar";
import LayoutContainer from "components/Layout/Container";
import {
  CardWrapper,
  OverflowLoader,
  PageControl,
  ScrollLoader,
  Search,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
} from "./styles";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import InfiniteScroll from "react-infinite-scroll-component";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import ActiveProgramCard from "./ActiveProgramCard/ActiveProgramCard";
import ROUTES from "navigation/routes";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};
const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;
@observer
class ActiveProgram extends React.Component<Props> {
  @computed get items() {
    return ActiveProgramStore.items;
  }

  @computed get isListEmpty() {
    return this.items.length === 0;
  }

  componentDidMount() {
    ActiveProgramStore.fetch();
  }

  componentWillUnmount() {
    ActiveProgramStore.resetPage();
  }

  fetchMoreItems = async () => {
    const { setMessage } = this.props;

    try {
      await ActiveProgramStore.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ActiveProgramStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && ActiveProgramStore.fetch();
  };

  onSearchClear = () => {
    ActiveProgramStore.clearSearch();
    ActiveProgramStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    if (value.column === "created") {
      const toUpper =
        value.column[0].toUpperCase() + value.column.slice(1).toLowerCase();
      value.column = toUpper;
    } else {
      value.column = "Name";
    }
    ActiveProgramStore.applyOrder(value);
    ActiveProgramStore.resetPage();
    ActiveProgramStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ActiveProgramStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ActiveProgramStore.resetPage();
    ActiveProgramStore.fetch();
  };

  onFilterClear = () => {
    ActiveProgramStore.clearFilters();
    ActiveProgramStore.fetch();
  };

  onAddItemClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS, {
      id: item.id,
    });
    this.props.history.push({
      pathname: path,
      state: {
        editMode: false,
        workoutSequenceName: item.name,
        workoutSequenceId: item.workoutSequenceId,
      },
    });
  };

  onEdit = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS, {
      id: item.id,
    });
    this.props.history.push({
      pathname: path,
      state: {
        editMode: true,
        workoutSequenceName: item.name,
        workoutSequenceId: item.workoutSequenceId,
      },
    });
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={"Active Programs"}
          controls={
            <>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddItemClick}
                >
                  {"Add Active Program"}
                </Button>
              </PageControl>
            </>
          }
          isLoading={ActiveProgramStore.isLoading}
        >
          <SearchContainer>
            <Search
              initValue={ActiveProgramStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={ActiveProgramStore.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={ActiveProgramStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  activeProgramTypeFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </SearchContainer>

          {(ActiveProgramStore.isProgramPublishing ||
            ActiveProgramStore.isProgramCopying) && <OverflowLoader />}
          {!this.isListEmpty ? (
            <InfiniteScroll
              dataLength={this.items.length}
              hasMore={ActiveProgramStore.hasMoreInfo}
              next={this.fetchMoreItems}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              {this.items.map(item => (
                <CardWrapper key={item.id}>
                  <ActiveProgramCard
                    title={item.name}
                    onDetailsClick={this.onDetailsClick(item)}
                    onEdit={this.onEdit(item)}
                  />
                </CardWrapper>
              ))}
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                ActiveProgramStore.search
                  ? "No matches found. Please try another search."
                  : "No active programs added yet."
              }
            />
          )}
        </Page>
      </LayoutContainer>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(ActiveProgram)));
